/**
 * Construction des URL d'appel API.
 *
 * Les routes sont declarees dans apiRoutes.ts sous forme de gabarits dont les parametres de
 * chemin sont prefixes par ':' (ex: `.../getformviewer/:forThisUPN`). Substituer ces parametres
 * a la main (String.replace) a produit des comportements divergents d'un appel a l'autre:
 * certains encodaient la valeur, d'autres non.
 *
 * Une valeur non encodee contenant '#', '?' ou '/' tronque ou deroute silencieusement la
 * requete. Cas concret: un UPN invite Entra ID de la forme
 * `prenom.nom_societe.com#EXT#@tenant.onmicrosoft.com` est coupe au '#', le serveur ne recoit
 * qu'une partie de l'identifiant.
 *
 * Ce module est le point de construction unique: il encode chaque parametre de chemin, assemble
 * la query string, et echoue immediatement si un parametre du gabarit n'a pas ete fourni - au
 * lieu d'envoyer au serveur une URL contenant encore un ':parametre' litteral.
 */

/**
 * Valeurs acceptees pour un parametre de chemin ou de query.
 */
export type ApiUrlParamValue = string | number | boolean | null | undefined;

/**
 * Detecte un parametre de gabarit `:name` restant.
 *
 * Le premier caractere doit etre une lettre ou un underscore, afin de ne pas confondre avec le
 * ':' d'un schema (`https://`) ou d'un numero de port (`:8080`).
 */
const REMAINING_PARAM_PATTERN = /:[A-Za-z_][A-Za-z0-9_]*/g;

/**
 * Construit l'expression qui cible un parametre precis. Le lookahead evite qu'un parametre court
 * ne morde sur un parametre plus long commencant pareil (`:formName` / `:formNameBis`).
 *
 * @param name Le nom du parametre, sans le ':'.
 */
const buildParamPattern = (name: string): RegExp => new RegExp(`:${name}(?![A-Za-z0-9_])`, 'g');

/**
 * Construit l'URL d'un appel API a partir d'un gabarit de apiRoutes.ts.
 *
 * @param template Le gabarit de route (ex: API_GET_FORM_LIST).
 * @param pathParams Les parametres de chemin, indexes par leur nom sans le ':'. Chaque valeur est
 * encodee. Une valeur absente ou vide leve une erreur: un parametre de chemin est obligatoire, et
 * une route dont un segment est optionnel doit etre declaree en deux gabarits distincts.
 * @param queryParams Les parametres de query string. Les valeurs absentes sont ignorees. Le
 * gabarit peut deja porter une query string, auquel cas les parametres sont ajoutes a la suite.
 * @returns L'URL prete a etre passee au client HTTP.
 */
export const buildApiUrl = (
  template: string,
  pathParams?: Record<string, ApiUrlParamValue>,
  queryParams?: Record<string, ApiUrlParamValue>,
): string => {
  let url = template;

  if (pathParams) {
    Object.entries(pathParams).forEach(([name, value]) => {
      if (value === null || value === undefined || value === '') {
        throw new Error(
          `buildApiUrl: le parametre de chemin ":${name}" est obligatoire mais n'a pas ete fourni pour le gabarit "${template}"`,
        );
      }

      // Replacer sous forme de fonction: la valeur encodee est ainsi inseree telle quelle, sans
      // interpretation des motifs de remplacement ($&, $1...).
      url = url.replace(buildParamPattern(name), () => encodeURIComponent(String(value)));
    });
  }

  const remainingParams = url.match(REMAINING_PARAM_PATTERN);

  if (remainingParams !== null) {
    throw new Error(
      `buildApiUrl: parametre(s) de chemin non fourni(s) pour le gabarit "${template}": ${remainingParams.join(', ')}`,
    );
  }

  if (queryParams) {
    const searchParams = new URLSearchParams();

    Object.entries(queryParams).forEach(([name, value]) => {
      if (value === null || value === undefined) return;

      searchParams.append(name, String(value));
    });

    const queryString = searchParams.toString();

    if (queryString !== '') url = `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
  }

  return url;
};

export default buildApiUrl;
